import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { completeOnboarding, verifyJira } from '../utils/onboardingApi';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Groups,
  Casino,
  Landscape,
  ArrowForward,
  Check,
} from '@mui/icons-material';

// Dark theme colors matching the auth/marketing pages.
const colors = {
  bgPrimary: '#0a0a0a',
  bgSecondary: '#141414',
  bgSurface: '#1a1a1a',
  border: '#2a2a2a',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  accentPrimary: '#3b82f6',
  accentSecondary: '#8b5cf6',
  success: '#22c55e',
  error: '#ef4444',
};

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: colors.bgPrimary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
});

const Card = styled(Box)({
  backgroundColor: colors.bgSurface,
  border: `1px solid ${colors.border}`,
  borderRadius: '16px',
  padding: '40px',
  width: '100%',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.bgSecondary,
    color: colors.textPrimary,
    borderRadius: '8px',
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: colors.accentPrimary },
    '&.Mui-focused fieldset': { borderColor: colors.accentPrimary },
  },
  '& .MuiInputLabel-root': {
    color: colors.textSecondary,
    '&.Mui-focused': { color: colors.accentPrimary },
  },
});

const GradientButton = styled(Button)({
  background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '1rem',
  '&:hover': {
    background: `linear-gradient(135deg, ${colors.accentPrimary} 20%, ${colors.accentSecondary} 100%)`,
    boxShadow: `0 4px 20px rgba(59, 130, 246, 0.4)`,
  },
  '&:disabled': {
    background: colors.border,
    color: colors.textMuted,
  },
});

// A large selectable methodology card. `selected` drives the accent highlight.
const ChoiceCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ selected }) => ({
  flex: 1,
  minWidth: 0,
  cursor: 'pointer',
  backgroundColor: colors.bgSecondary,
  border: `2px solid ${selected ? colors.accentPrimary : colors.border}`,
  borderRadius: '12px',
  padding: '28px 24px',
  transition: 'border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
  position: 'relative',
  '&:hover': {
    borderColor: colors.accentPrimary,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
  },
}));

const METHODOLOGIES = [
  {
    key: 'scrum',
    title: 'Scrum',
    tagline: 'Sprints, story points, planning poker',
    icon: Casino,
    bullets: [
      'Real-time planning poker estimation',
      'Story points and velocity tracking',
      'Sprint-based team workflow',
    ],
  },
  {
    key: 'shape-up',
    title: 'Shape Up',
    tagline: 'Cycles, bets, hill charts',
    icon: Landscape,
    bullets: [
      'Fixed cycles with appetite-driven bets',
      'Pitches and shaping work',
      'Hill charts to track progress',
    ],
  },
];

const OnboardingPage = () => {
  const { user, refreshUser } = useAuth();
  const { refreshOrganizations } = useWorkspace();
  const navigate = useNavigate();

  // Prefill a friendly default workspace name from the user's first name.
  const defaultName = user?.name ? `${user.name.split(' ')[0]}'s Workspace` : '';
  const [step, setStep] = useState(1);
  const [organizationName, setOrganizationName] = useState(defaultName);
  const [methodology, setMethodology] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Optional Jira connection (step 3).
  const [jiraUrl, setJiraUrl] = useState('');
  const [jiraUsername, setJiraUsername] = useState('');
  const [jiraApiToken, setJiraApiToken] = useState('');
  const [jiraProjectKey, setJiraProjectKey] = useState('');
  const [jiraStoryPointFieldId, setJiraStoryPointFieldId] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [jiraError, setJiraError] = useState('');

  const goToStep2 = (e) => {
    e.preventDefault();
    if (!organizationName.trim()) {
      setError('Please give your workspace a name.');
      return;
    }
    setError('');
    setStep(2);
  };

  const goToStep3 = () => {
    if (!methodology) {
      setError('Pick how your team works to continue.');
      return;
    }
    setError('');
    setStep(3);
  };

  // Create the org (optionally with Jira), clear the onboarding gate, adopt the
  // new workspace, and route into the chosen experience.
  const finishOnboarding = async (jira) => {
    await completeOnboarding({
      organizationName: organizationName.trim(),
      methodology,
      ...(jira ? { jira } : {}),
    });
    await refreshUser();
    await refreshOrganizations();
    navigate(methodology === 'shape-up' ? '/shape-up' : '/dashboard');
  };

  const handleSkip = async () => {
    setError('');
    setJiraError('');
    setSubmitting(true);
    try {
      await finishOnboarding();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Something went wrong setting up your workspace. Please try again.'
      );
      setSubmitting(false);
    }
  };

  const handleConnectJira = async () => {
    setError('');
    setJiraError('');
    setVerifying(true);
    try {
      await verifyJira({
        url: jiraUrl.trim(),
        username: jiraUsername.trim(),
        apiToken: jiraApiToken.trim(),
      });
    } catch (err) {
      setJiraError(
        err.response?.data?.message ||
          err.message ||
          "Couldn't verify Jira connection. Check the details and try again."
      );
      setVerifying(false);
      return;
    }

    // Verified — now create the org with the Jira connection attached.
    try {
      await finishOnboarding({
        url: jiraUrl.trim(),
        username: jiraUsername.trim(),
        apiToken: jiraApiToken.trim(),
        projectKey: jiraProjectKey.trim() || undefined,
        storyPointFieldId: jiraStoryPointFieldId.trim() || undefined,
      });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Something went wrong setting up your workspace. Please try again.'
      );
      setVerifying(false);
    }
  };

  const jiraReady =
    jiraUrl.trim() && jiraUsername.trim() && jiraApiToken.trim();
  const busy = submitting || verifying;

  return (
    <PageWrapper>
      <Container maxWidth={step === 1 ? 'sm' : 'md'}>
        <Card>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <Groups sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 1 }}>
              {step === 1 && 'Welcome! Let’s set up your workspace'}
              {step === 2 && 'How does your team work?'}
              {step === 3 && 'Connect Jira (optional)'}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.textSecondary }}>
              {step === 1 &&
                'Name the workspace where your team will plan and track work.'}
              {step === 2 &&
                'Choose the approach that fits your team. You can enable the other later in settings.'}
              {step === 3 &&
                'Link your Jira site to import issues and sync work. You can skip this and set it up later in settings.'}
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: colors.error,
                border: `1px solid ${colors.error}`,
                '& .MuiAlert-icon': { color: colors.error },
              }}
            >
              {error}
            </Alert>
          )}

          {step === 1 && (
            <Box component="form" onSubmit={goToStep2} noValidate>
              <StyledTextField
                required
                fullWidth
                autoFocus
                id="organizationName"
                label="Workspace name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                disabled={submitting}
              />
              <GradientButton
                type="submit"
                fullWidth
                endIcon={<ArrowForward />}
                sx={{ mt: 3 }}
                disabled={submitting}
              >
                Continue
              </GradientButton>
            </Box>
          )}

          {step === 2 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 4,
                }}
              >
                {METHODOLOGIES.map((m) => {
                  const Icon = m.icon;
                  const selected = methodology === m.key;
                  return (
                    <ChoiceCard
                      key={m.key}
                      selected={selected}
                      onClick={() => setMethodology(m.key)}
                      role="button"
                      aria-pressed={selected}
                    >
                      {selected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: colors.accentPrimary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Check sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      )}
                      <Icon sx={{ fontSize: 40, color: colors.accentPrimary, mb: 1.5 }} />
                      <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 0.5 }}>
                        {m.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                        {m.tagline}
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {m.bullets.map((b) => (
                          <Typography
                            key={b}
                            component="li"
                            variant="body2"
                            sx={{ color: colors.textMuted, mb: 0.75 }}
                          >
                            {b}
                          </Typography>
                        ))}
                      </Box>
                    </ChoiceCard>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={() => {
                    setError('');
                    setStep(1);
                  }}
                  disabled={submitting}
                  sx={{ color: colors.textSecondary }}
                >
                  Back
                </Button>
                <GradientButton
                  onClick={goToStep3}
                  fullWidth
                  endIcon={<ArrowForward />}
                  disabled={!methodology}
                >
                  Continue
                </GradientButton>
              </Box>
            </Box>
          )}

          {step === 3 && (
            <Box>
              {jiraError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: colors.error,
                    border: `1px solid ${colors.error}`,
                    '& .MuiAlert-icon': { color: colors.error },
                  }}
                >
                  {jiraError}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
                <StyledTextField
                  fullWidth
                  autoFocus
                  label="Jira URL"
                  placeholder="https://your-org.atlassian.net"
                  value={jiraUrl}
                  onChange={(e) => setJiraUrl(e.target.value)}
                  disabled={busy}
                />
                <StyledTextField
                  fullWidth
                  label="Email / username"
                  value={jiraUsername}
                  onChange={(e) => setJiraUsername(e.target.value)}
                  disabled={busy}
                />
                <StyledTextField
                  fullWidth
                  type="password"
                  label="API token"
                  value={jiraApiToken}
                  onChange={(e) => setJiraApiToken(e.target.value)}
                  disabled={busy}
                />
                <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <StyledTextField
                    fullWidth
                    label="Project key (optional)"
                    value={jiraProjectKey}
                    onChange={(e) => setJiraProjectKey(e.target.value)}
                    disabled={busy}
                  />
                  <StyledTextField
                    fullWidth
                    label="Story point field ID (optional)"
                    value={jiraStoryPointFieldId}
                    onChange={(e) => setJiraStoryPointFieldId(e.target.value)}
                    disabled={busy}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  Connecting Jira is optional — you can add or change these details
                  anytime in your workspace settings.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  onClick={() => {
                    setError('');
                    setJiraError('');
                    setStep(2);
                  }}
                  disabled={busy}
                  sx={{ color: colors.textSecondary }}
                >
                  Back
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  onClick={handleSkip}
                  disabled={busy}
                  sx={{ color: colors.textSecondary, textTransform: 'none' }}
                >
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: colors.textSecondary }} />
                  ) : (
                    'Skip for now'
                  )}
                </Button>
                <GradientButton
                  onClick={handleConnectJira}
                  disabled={busy || !jiraReady}
                >
                  {verifying ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'Connect Jira'
                  )}
                </GradientButton>
              </Box>
            </Box>
          )}
        </Card>
      </Container>
    </PageWrapper>
  );
};

export default OnboardingPage;
